import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const initialsOf = (n) => (n || "?").split(" ").filter(Boolean).map(s => s[0]).slice(0,2).join("").toUpperCase();

const isSSMCH = (inst) => {
  if (!inst) return false;
  const lower = inst.toLowerCase();
  return lower.includes("sukh sagar") || lower.includes("ssmch");
};

const parseDateSafe = (d) => {
  if (!d) return null;
  // Parse YYYY-MM-DD as local time to avoid UTC timezone shift
  const parts = String(d).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (parts) return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  const t = new Date(d);
  return isNaN(t.getTime()) ? null : t;
};

const fmtDate = (d) => {
  const dt = parseDateSafe(d);
  if (!dt) return d || "—";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const calcDuration = (from, to) => {
  const a = parseDateSafe(from);
  const b = to ? parseDateSafe(to) : new Date();
  if (!a || !b) return "";
  let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (months < 0) months = 0;
  const yrs = Math.floor(months / 12);
  const rem = months % 12;
  const suffix = !to ? "+" : "";
  if (yrs > 0 && rem > 0) return `${yrs}y ${rem}m${suffix}`;
  if (yrs > 0) return `${yrs}y${suffix}`;
  return `${rem}m${suffix}`;
};

/* Build the current SSMCH position entry from the faculty record */
const buildCurrentPosition = (f) => {
  if (!f.designation || !f.joined_at) return null;
  // Derive start date: day after the latest SSMCH past_experience to_date, or joined_at
  let fromDate = f.joined_at;
  const ssmchExps = (f.past_experience || []).filter(e => isSSMCH(e.institution) && e.to_date);
  if (ssmchExps.length > 0) {
    const latest = ssmchExps.reduce((max, e) => {
      const d = parseDateSafe(e.to_date);
      return d && (!max || d > max) ? d : max;
    }, null);
    if (latest) {
      const next = new Date(latest.getFullYear(), latest.getMonth(), latest.getDate() + 1);
      const yyyy = next.getFullYear();
      const mm = String(next.getMonth() + 1).padStart(2, '0');
      const dd = String(next.getDate()).padStart(2, '0');
      fromDate = `${yyyy}-${mm}-${dd}`;
    }
  }
  return {
    designation: f.designation,
    institution: "Sukh Sagar Medical College & Hospital, Jabalpur",
    from_date: fromDate,
    to_date: null,
    nature_of_employment: "Full-time",
    is_current_position: true,
    department: f.department,
  };
};

/* Extract implied promotions from past_experience at SSMCH, including current position */
const extractSSMCHPromotions = (pastExp, currentPos) => {
  const allEntries = [...(pastExp || [])];
  if (currentPos) allEntries.push(currentPos);
  if (allEntries.length === 0) return [];
  const ssmchEntries = allEntries
    .filter(e => isSSMCH(e.institution))
    .sort((a, b) => (parseDateSafe(a.from_date) || 0) - (parseDateSafe(b.from_date) || 0));
  const promos = [];
  for (let i = 1; i < ssmchEntries.length; i++) {
    const prev = ssmchEntries[i - 1];
    const curr = ssmchEntries[i];
    if (prev.designation && curr.designation && prev.designation !== curr.designation) {
      promos.push({
        promotion_id: `pe-promo-${i}`,
        promotion_date: curr.from_date,
        previous_designation: prev.designation,
        new_designation: curr.designation,
        notes: curr.is_current_position ? "Current appointment at SSMCH" : "Inferred from uploaded past experience",
        from_past_experience: true,
        nmc_recognised_prev: prev.nmc_recognised,
        nmc_recognised_new: curr.nmc_recognised,
      });
    }
  }
  return promos;
};

// Indian-style currency formatter (₹1,20,000)
const inr = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return "₹" + num.toLocaleString("en-IN");
};

function ReadinessCell({ ok, label, val }) {
  return (
    <div className={`ready-cell ${ok ? 'ok' : 'no'}`}>
      <div className="rc-label">{label}</div>
      <div className="rc-val">{val}</div>
    </div>
  );
}

// ===== LIFE-EVENT FORMS =====

const ACCOM_TYPES = ["Room (shared)","Room (single occupancy)","1 BHK","2 BHK","3 BHK","Cottage / Bungalow"];
const FURNISHED_OPTS = ["Furnished","Semi-furnished","Unfurnished"];
const MESS_PLANS = ["Vegetarian","Non-Vegetarian","Both","Special / Dietary"];
const MEALS_OPTS = ["Breakfast","Lunch","Dinner"];
const SHARED_TYPES = ["Room (shared)","Room (single occupancy)"];

function PromotionForm({ faculty, onSaved, onCancel }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [newDesig, setNewDesig] = useState(faculty.designation === "Assistant Professor" ? "Associate Professor" : "Professor");
  const [newDept, setNewDept] = useState(faculty.department || "");
  const [newSalary, setNewSalary] = useState("");
  const [joiningRef, setJoiningRef] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [depts, setDepts] = useState([]);

  useEffect(() => {
    api.get("/reference/departments").then(({ data }) => setDepts(data.map(d => d.department))).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/faculty/${faculty.faculty_id}/promotion`, {
        promotion_date: date, new_designation: newDesig,
        new_department: newDept || null,
        new_salary: newSalary ? Number(newSalary) : null,
        joining_report_ref: joiningRef || null,
        notes: notes || null,
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="form-section" data-testid="form-promotion" style={{margin:0}}>
      <div style={{fontSize:12, color:'var(--text-2)', marginBottom:8}}>
        <strong>Cascade preview</strong> — closes current position register, opens new entry with new designation, updates salary, logs to promotion table (c1).
      </div>
      <div className="form-grid">
        <div className="form-field"><label className="form-label">Promotion Date <span className="req">*</span></label><input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} data-testid="promotion-date"/></div>
        <div className="form-field"><label className="form-label">New Designation <span className="req">*</span></label>
          <select className="form-select" value={newDesig} onChange={e => setNewDesig(e.target.value)} data-testid="promotion-desig">
            <option>Dean</option><option>Professor-HOD</option><option>Professor</option><option>Associate Professor</option><option>Assistant Professor</option><option>Senior Resident</option><option>Junior Resident</option><option>Demonstrator</option><option>Tutor</option>
          </select>
        </div>
        <div className="form-field"><label className="form-label">Department</label>
          <select className="form-select" value={newDept} onChange={e => setNewDept(e.target.value)} data-testid="promotion-dept">
            <option value="">Same as current</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {faculty.department && <div className="form-hint">Current: {faculty.department}</div>}
        </div>
        <div className="form-field"><label className="form-label">New Monthly Salary</label>
          <div style={{position:'relative'}}>
            <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>₹</span>
            <input type="number" className="form-input mono" style={{paddingLeft:26}} placeholder={faculty.monthly_salary ? String(faculty.monthly_salary) : "Enter new salary"} value={newSalary} onChange={e => setNewSalary(e.target.value)} data-testid="promotion-salary"/>
          </div>
          {faculty.monthly_salary && <div className="form-hint">Current: ₹{Number(faculty.monthly_salary).toLocaleString('en-IN')}</div>}
        </div>
        <div className="form-field"><label className="form-label">Joining Report Reference #</label><input className="form-input mono" placeholder="e.g. JR/2026/045" value={joiningRef} onChange={e => setJoiningRef(e.target.value)} data-testid="promotion-joining-ref"/></div>
        <div className="form-field"><label className="form-label">Notification / Notes</label><input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} data-testid="promotion-notes"/></div>
      </div>
      {err && <div style={{color:'var(--bad)', fontSize:12, marginTop:10}}>{err}</div>}
      <div style={{display:'flex', gap:8, marginTop:14}}>
        <button className="btn primary" disabled={busy} onClick={submit} data-testid="promotion-save">{busy ? 'Saving…' : 'Confirm Promotion'}</button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function SalaryForm({ faculty, onSaved, onCancel }) {
  const [eff, setEff] = useState(new Date().toISOString().slice(0,10));
  const [newSalary, setNewSalary] = useState("");
  const [reason, setReason] = useState("Annual Increment");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/faculty/${faculty.faculty_id}/salary-increment`, {
        effective_from: eff, new_salary: Number(newSalary), reason
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="form-section" data-testid="form-salary" style={{margin:0}}>
      <div className="form-grid">
        <div className="form-field"><label className="form-label">Effective From <span className="req">*</span></label><input type="date" className="form-input" value={eff} onChange={e => setEff(e.target.value)} data-testid="salary-date"/></div>
        <div className="form-field"><label className="form-label">New Salary <span className="req">*</span></label>
          <div style={{position:'relative'}}>
            <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>₹</span>
            <input type="number" className="form-input mono" style={{paddingLeft:26}} value={newSalary} onChange={e => setNewSalary(e.target.value)} data-testid="salary-amount"/>
          </div>
          {faculty.monthly_salary && <div className="form-hint">Current: ₹{Number(faculty.monthly_salary).toLocaleString('en-IN')} · IFSC: {faculty.ifsc_code || '—'} · A/C: {faculty.account_number ? '…'+faculty.account_number.slice(-4) : '—'}</div>}
        </div>
        <div className="form-field span-2"><label className="form-label">Reason</label>
          <select className="form-select" value={reason} onChange={e => setReason(e.target.value)} data-testid="salary-reason">
            {["Annual Increment","Inflation Adjustment","Counter-offer Match","DA Revision","Trust Discretion","Promotion-linked","Other"].map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
      </div>
      {err && <div style={{color:'var(--bad)', fontSize:12, marginTop:10}}>{err}</div>}
      <div style={{display:'flex', gap:8, marginTop:14}}>
        <button className="btn primary" disabled={busy || !newSalary} onClick={submit} data-testid="salary-save">{busy ? 'Saving…' : 'Log Increment'}</button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function AbscondForm({ faculty, onSaved, onCancel }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [reason, setReason] = useState("");
  const [aebas, setAebas] = useState(true);
  const [nmcRemoved, setNmcRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/faculty/${faculty.faculty_id}/abscond`, {
        last_attendance_date: date, reason: reason || null, aebas_block: aebas, nmc_removed: nmcRemoved
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="form-section" data-testid="form-abscond" style={{margin:0}}>
      <div style={{background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.3)', padding:10, borderRadius:8, fontSize:12, color:'#991b1b', marginBottom:14}}>
        <strong>State machine</strong> — Marking absconded sets status to <strong>Absconded</strong>{aebas && <>, initiates <strong>AEBAS block</strong> on NMC portal</>}. Relieving is blocked until AEBAS is unblocked.
        {faculty.aebas_id && <div style={{marginTop:4}}>AEBAS ID: <strong className="mono">{faculty.aebas_id}</strong></div>}
        {faculty.greyt_hr_id && <div>GreytHR ID: <strong className="mono">{faculty.greyt_hr_id}</strong> — payroll will be paused.</div>}
      </div>
      <div className="form-grid">
        <div className="form-field"><label className="form-label">Last Attendance Date <span className="req">*</span></label><input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} data-testid="abscond-date"/></div>
        <div className="form-field">
          <label className="form-label">AEBAS Action</label>
          <div style={{display:'flex', flexDirection:'column', gap:6, fontSize:13, paddingTop:8}}>
            <label><input type="checkbox" checked={aebas} onChange={e => setAebas(e.target.checked)}/> Initiate AEBAS block on NMC portal</label>
            <label><input type="checkbox" checked={nmcRemoved} onChange={e => setNmcRemoved(e.target.checked)} data-testid="abscond-nmc-removed"/> AEBAS ID removed from NMC Database</label>
          </div>
        </div>
        <div className="form-field span-2"><label className="form-label">Reason / Details</label><textarea className="form-input" placeholder="Describe circumstances — absent since X, no communication, etc." value={reason} onChange={e => setReason(e.target.value)} data-testid="abscond-reason"/></div>
      </div>
      {err && <div style={{color:'var(--bad)', fontSize:12, marginTop:10}}>{err}</div>}
      <div style={{display:'flex', gap:8, marginTop:14}}>
        <button className="btn primary" disabled={busy} onClick={submit} style={{background:'#dc2626', borderColor:'#dc2626',color:'#fff'}} data-testid="abscond-save">{busy ? 'Saving…' : 'Mark Absconded'}</button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function AebasUnblockForm({ faculty, onSaved, onCancel }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [dues, setDues] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/faculty/${faculty.faculty_id}/aebas-unblock`, { unblock_date: date, dues_cleared: dues });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="form-section" data-testid="form-aebas" style={{margin:0}}>
      {faculty.aebas_id && <div className="form-hint" style={{marginBottom:8}}>AEBAS ID: <strong className="mono">{faculty.aebas_id}</strong></div>}
      <div className="form-grid">
        <div className="form-field"><label className="form-label">Unblock Date <span className="req">*</span></label><input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)}/></div>
        <div className="form-field"><label className="form-label">Dues</label>
          <label style={{display:'flex', gap:8, alignItems:'center', fontSize:13, paddingTop:8}}>
            <input type="checkbox" checked={dues} onChange={e => setDues(e.target.checked)}/>All dues cleared
          </label>
        </div>
      </div>
      {err && <div style={{color:'var(--bad)', fontSize:12, marginTop:10}}>{err}</div>}
      <div style={{display:'flex', gap:8, marginTop:14}}>
        <button className="btn primary" disabled={busy} onClick={submit} data-testid="aebas-save">{busy ? 'Saving…' : 'Unblock AEBAS'}</button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function RelieveForm({ faculty, onSaved, onCancel }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [reason, setReason] = useState("Resignation");
  const [noDues, setNoDues] = useState(false);
  const [idReturned, setIdReturned] = useState(false);
  const [emailSusp, setEmailSusp] = useState(false);
  const [nmcRemoved, setNmcRemoved] = useState(false);
  const [accomVacated, setAccomVacated] = useState(false);
  const [greytDeact, setGreytDeact] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const hasAccom = faculty.accommodation && faculty.accommodation.accommodation_type;
  const hasGreyt = !!faculty.greyt_hr_id;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/faculty/${faculty.faculty_id}/relieve`, {
        relieving_date: date, reason,
        no_dues_confirmed: noDues, id_card_returned: idReturned, email_suspended: emailSusp,
        nmc_removed: nmcRemoved,
        accommodation_vacated: accomVacated, greythr_deactivated: greytDeact,
        notes: notes || null
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="form-section" data-testid="form-relieve" style={{margin:0}}>
      <div className="form-grid">
        <div className="form-field"><label className="form-label">Relieving Date <span className="req">*</span></label><input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} data-testid="relieve-date"/></div>
        <div className="form-field"><label className="form-label">Reason <span className="req">*</span></label>
          <select className="form-select" value={reason} onChange={e => setReason(e.target.value)} data-testid="relieve-reason">
            {["Resignation","Contract end","Retirement","Superannuation (age 65+)","Termination","Mutual Separation","Transfer out","Bond completed"].map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="form-field span-2">
          <label className="form-label">Exit Checklist</label>
          <div style={{display:'flex', flexDirection:'column', gap:6, fontSize:13, marginTop:4}}>
            <label><input type="checkbox" checked={noDues} onChange={e => setNoDues(e.target.checked)}/> No-dues certificate confirmed</label>
            <label><input type="checkbox" checked={idReturned} onChange={e => setIdReturned(e.target.checked)}/> ID card returned {faculty.id_card_status === 'Issued' && <span style={{fontSize:11,color:'var(--text-3)'}}>(currently: Issued)</span>}</label>
            <label><input type="checkbox" checked={emailSusp} onChange={e => setEmailSusp(e.target.checked)}/> Official email ({faculty.official_email || '@ssmch.in'}) suspended</label>
            <label><input type="checkbox" checked={nmcRemoved} onChange={e => setNmcRemoved(e.target.checked)} data-testid="relieve-nmc-removed"/> AEBAS ID removed from NMC Database</label>
            {hasAccom && <label><input type="checkbox" checked={accomVacated} onChange={e => setAccomVacated(e.target.checked)}/> Accommodation vacated ({faculty.accommodation.accommodation_type} · {faculty.accommodation.block} {faculty.accommodation.quarter_number})</label>}
            {hasGreyt && <label><input type="checkbox" checked={greytDeact} onChange={e => setGreytDeact(e.target.checked)}/> GreytHR ID ({faculty.greyt_hr_id}) deactivated</label>}
          </div>
        </div>
        <div className="form-field span-2"><label className="form-label">Notes</label><textarea className="form-input" placeholder="Exit remarks, handover status, pending items…" value={notes} onChange={e => setNotes(e.target.value)} data-testid="relieve-notes"/></div>
      </div>
      {err && <div style={{color:'var(--bad)', fontSize:12, marginTop:10}}>{err}</div>}
      <div style={{display:'flex', gap:8, marginTop:14}}>
        <button className="btn primary" disabled={busy} onClick={submit} style={{background:'#dc2626',borderColor:'#dc2626',color:'#fff'}} data-testid="relieve-save">{busy ? 'Saving…' : 'Initiate Relieving'}</button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function AccommodationForm({ faculty, onSaved, onCancel }) {
  const [type, setType] = useState("");
  const [block, setBlock] = useState("");
  const [quarter, setQuarter] = useState("");
  const [furnished, setFurnished] = useState("Semi-furnished");
  const [allotDate, setAllotDate] = useState(new Date().toISOString().slice(0,10));
  const [moveIn, setMoveIn] = useState(new Date().toISOString().slice(0,10));
  const [rent, setRent] = useState("0");
  const [deposit, setDeposit] = useState("");
  const [mess, setMess] = useState(false);
  const [messPlan, setMessPlan] = useState("Both");
  const [meals, setMeals] = useState(["Breakfast","Lunch","Dinner"]);
  const [messDed, setMessDed] = useState("");
  const [familyCount, setFamilyCount] = useState("1");
  const [pets, setPets] = useState("None");
  const [petsDetail, setPetsDetail] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        accommodation_type: type, block, quarter_number: quarter, furnished_status: furnished,
        allotment_date: allotDate, move_in_date: moveIn,
        monthly_rent: Number(rent) || 0,
        mess_service: mess,
        family_members_count: Number(familyCount) || 1,
      };
      if (deposit) payload.security_deposit = Number(deposit);
      if (mess) { payload.mess_plan = messPlan; payload.meals_covered = meals; if (messDed) payload.mess_deduction = Number(messDed); }
      if (pets !== "None") { payload.pets = pets; if (pets === "Yes (other)") payload.pets_detail = petsDetail; }
      if (notes) payload.notes = notes;
      await api.post(`/faculty/${faculty.faculty_id}/accommodation`, payload);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="form-section" data-testid="form-accommodation" style={{margin:0}}>
      <div style={{fontSize:12, color:'var(--text-2)', marginBottom:10}}>
        <strong>Re-allotment</strong> — this closes the current allotment (if any) and creates a new one.
      </div>
      <div className="form-grid">
        <div className="form-field"><label className="form-label">Accommodation Type <span className="req">*</span></label>
          <select className="form-select" value={type} onChange={e => { setType(e.target.value); setMess(SHARED_TYPES.includes(e.target.value)); }} data-testid="accom-ev-type">
            <option value="">Select</option>
            {ACCOM_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-field"><label className="form-label">Block / Wing <span className="req">*</span></label>
          <input className="form-input" placeholder="e.g. Block A" value={block} onChange={e => setBlock(e.target.value)} data-testid="accom-ev-block"/>
        </div>
        <div className="form-field"><label className="form-label">Quarter / Flat <span className="req">*</span></label>
          <input className="form-input" placeholder="e.g. 104, A-12" value={quarter} onChange={e => setQuarter(e.target.value)} data-testid="accom-ev-quarter"/>
        </div>
        <div className="form-field"><label className="form-label">Furnished</label>
          <select className="form-select" value={furnished} onChange={e => setFurnished(e.target.value)}>{FURNISHED_OPTS.map(f => <option key={f}>{f}</option>)}</select>
        </div>
        <div className="form-field"><label className="form-label">Allotment Date <span className="req">*</span></label><input type="date" className="form-input" value={allotDate} onChange={e => setAllotDate(e.target.value)} data-testid="accom-ev-allotdate"/></div>
        <div className="form-field"><label className="form-label">Move-in Date <span className="req">*</span></label><input type="date" className="form-input" value={moveIn} onChange={e => setMoveIn(e.target.value)} data-testid="accom-ev-movein"/></div>
        <div className="form-field"><label className="form-label">Monthly Rent</label>
          <div style={{position:'relative'}}><span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>₹</span>
          <input type="number" min="0" className="form-input mono" style={{paddingLeft:26}} value={rent} onChange={e => setRent(e.target.value)}/></div>
        </div>
        <div className="form-field"><label className="form-label">Security Deposit</label>
          <div style={{position:'relative'}}><span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>₹</span>
          <input type="number" min="0" className="form-input mono" style={{paddingLeft:26}} placeholder="0" value={deposit} onChange={e => setDeposit(e.target.value)}/></div>
        </div>
        <div className="form-field span-2" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div><label className="form-label" style={{marginBottom:0}}>Mess Service</label><div className="form-hint">Default Yes for shared rooms</div></div>
          <label style={{position:'relative',display:'inline-block',width:38,height:20,cursor:'pointer'}}><input type="checkbox" checked={mess} onChange={e => setMess(e.target.checked)} style={{opacity:0,width:0,height:0}}/><span style={{position:'absolute',inset:0,borderRadius:20,background:mess?'#16a34a':'#cbd5e1',transition:'background 0.2s'}}><span style={{position:'absolute',top:2,left:mess?20:2,width:16,height:16,borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 4px rgba(0,0,0,.25)'}}/></span></label>
        </div>
        {mess && <>
          <div className="form-field"><label className="form-label">Mess Plan</label>
            <select className="form-select" value={messPlan} onChange={e => setMessPlan(e.target.value)}>{MESS_PLANS.map(p => <option key={p}>{p}</option>)}</select>
          </div>
          <div className="form-field"><label className="form-label">Meals</label>
            <div style={{display:'flex',gap:8,marginTop:4,flexWrap:'wrap'}}>
              {MEALS_OPTS.map(m => (
                <label key={m} style={{display:'flex',alignItems:'center',gap:5,fontSize:13,padding:'4px 8px',borderRadius:6,border:'1px solid var(--border)',cursor:'pointer',background:meals.includes(m)?'rgba(52,211,153,0.1)':'transparent'}}>
                  <input type="checkbox" checked={meals.includes(m)} onChange={e => { if (e.target.checked) setMeals([...meals, m]); else setMeals(meals.filter(x=>x!==m)); }}/>{m}
                </label>
              ))}
            </div>
          </div>
          <div className="form-field"><label className="form-label">Mess Deduction</label>
            <div style={{position:'relative'}}><span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--text-2)',fontFamily:'var(--mono)'}}>₹</span>
            <input type="number" min="0" className="form-input mono" style={{paddingLeft:26}} placeholder="0" value={messDed} onChange={e => setMessDed(e.target.value)}/></div>
          </div>
        </>}
        <div className="form-field"><label className="form-label">Family Members</label><input type="number" min="1" className="form-input mono" value={familyCount} onChange={e => setFamilyCount(e.target.value)}/></div>
        <div className="form-field span-2"><label className="form-label">Notes</label><textarea className="form-input" placeholder="Re-allotment reason, special arrangements, etc." style={{minHeight:48}} value={notes} onChange={e => setNotes(e.target.value)} data-testid="accom-ev-notes"/></div>
      </div>
      {err && <div style={{color:'var(--bad)', fontSize:12, marginTop:10}}>{err}</div>}
      <div style={{display:'flex', gap:8, marginTop:14}}>
        <button className="btn primary" disabled={busy || !type || !block || !quarter} onClick={submit} data-testid="accommodation-save">{busy ? 'Saving…' : 'Save Allotment'}</button>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ===== MAIN PROFILE =====

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "confidential";

  const [f, setF] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [history, setHistory] = useState(null);
  const [activeForm, setActiveForm] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/faculty/${id}`);
    setF(data);
    try {
      const r = await api.get("/stats/pipeline");
      setPipeline((r.data || []).find(p => p.faculty_id === id) || null);
    } catch { /* */ }
    try {
      const h = await api.get(`/faculty/${id}/history`);
      setHistory(h.data);
    } catch { /* */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!f) return <div style={{padding:40, textAlign:'center'}}><div className="spinner"/></div>;

  const promoChecks = pipeline?.checks || { post_pg:false, in_role:false, pubs:false, met:false, bmr:false };
  const promoDisp = pipeline?.display || { post_pg:'—', in_role:'—', pubs:'—', met:'—', bmr:'—' };

  // Indirect signals (spec §11)
  const signals = [
    { rule: "Greyt HR ID present",        ok: !!f.greyt_hr_id,                    implies: f.greyt_hr_id ? "Regular employee" : "May be visiting / consultant" },
    { rule: "AEBAS ID present",           ok: !!f.aebas_id && !f.aebas_blocked,   implies: (f.aebas_id && !f.aebas_blocked) ? "Under-70, biometric on file" : "Possibly over-70 OR blocked" },
    { rule: "Institutional accommodation",ok: (history?.accommodation || []).some(a => !a.vacate_date && a.accommodation_type && a.accommodation_type !== "None (own arrangement)"), implies: "Non-local origin" },
  ];

  const formSaved = async () => { setActiveForm(null); await load(); };
  const isAbsconded = f.status === "Absconded";
  const isTerminal = ["Relieved","Retired"].includes(f.status);

  return (
    <div data-testid="view-profile">
      <div className="topbar">
        <div>
          <h1 className="pg-title">Unified Faculty Profile</h1>
          <div className="pg-sub">Single screen — everything about one person</div>
        </div>
        <div className="topbar-meta">
          <span className="meta-chip" style={{cursor:'pointer'}} onClick={() => navigate('/')}>← All faculty</span>
        </div>
      </div>

      <div className="profile-hero">
        {f.photo_url ? (
          <img src={f.photo_url} alt={f.full_name} className="pf-av" style={{objectFit:'cover'}} data-testid="profile-photo"/>
        ) : (
          <div className="pf-av">{initialsOf(f.full_name)}</div>
        )}
        <div>
          <h2>{f.title} {f.full_name}</h2>
          <div className="pf-meta">
            <span><strong>{f.designation}</strong></span>
            <span>{f.department}</span>
            {f.age ? <span>Age {f.age}</span> : null}
            <span className={`badge ${f.status === 'Active' ? 'ok' : f.status === 'Absconded' ? 'bad' : 'warn'}`}>{f.status}</span>
            {f.aebas_blocked && <span className="badge bad">AEBAS Blocked</span>}
          </div>
        </div>
        <div className="id-badge">{f.ssmch_id}</div>
      </div>

      <div className="profile-cards">
        <div className="card">
          <div className="card-title">Identity &amp; ID Matrix · Table A1</div>
          <div className="kv-grid">
            <div className="kv-row"><div className="k">Full Name</div><div className="v sans">{f.full_name}</div></div>
            <div className="kv-row"><div className="k">Date of Birth</div><div className="v">{f.dob || '—'}</div></div>
            <div className="kv-row"><div className="k">Track</div><div className="v sans">{f.track}</div></div>
            <div className="kv-row"><div className="k">Joined</div><div className="v">{f.joined_at || '—'}</div></div>
            <div className="kv-row"><div className="k">Aadhaar</div><div className="v">{f.aadhaar || '—'}</div></div>
            <div className="kv-row"><div className="k">Personal Email</div><div className="v sans">{f.personal_email || '—'}</div></div>
            <div className="kv-row"><div className="k">Official Email</div><div className="v sans">{f.official_email || '—'}</div></div>
            <div className="kv-row"><div className="k">WhatsApp</div><div className="v">{f.whatsapp || '—'}</div></div>
            <div className="kv-row"><div className="k">Correspondence</div><div className="v sans" style={{whiteSpace:'pre-wrap'}}>{f.correspondence_address || '—'}</div></div>
            {!f.permanent_address_same && (
              <div className="kv-row"><div className="k">Permanent</div><div className="v sans" style={{whiteSpace:'pre-wrap'}}>{f.permanent_address || '—'}</div></div>
            )}
            {(f.aadhaar_card_url || f.pan_card_url) && (
              <div className="kv-row">
                <div className="k">Documents</div>
                <div className="v sans" style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                  {f.aadhaar_card_url && <a href={f.aadhaar_card_url} target="_blank" rel="noreferrer" className="meta-chip" data-testid="profile-aadhaar-doc">⎘ Aadhaar Card</a>}
                  {f.pan_card_url && <a href={f.pan_card_url} target="_blank" rel="noreferrer" className="meta-chip" data-testid="profile-pan-doc">⎘ PAN Card</a>}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-title">Qualifications &amp; Registration · Table B1</div>
          <div className="kv-grid">
            <div className="kv-row"><div className="k">UG</div><div className="v sans">{f.ug_course || f.ug || '—'} {f.ug_college ? `· ${f.ug_college}` : ''}</div></div>
            <div className="kv-row"><div className="k">PG</div><div className="v sans">{f.pg_title || ((f.pg_type && f.pg) ? `${f.pg_type} ${f.pg}` : (f.pg || '—'))} {f.pg_college ? `· ${f.pg_college}` : ''}</div></div>
            {f.has_ss && f.ss_title && (
              <div className="kv-row"><div className="k">Super-Specialty</div><div className="v sans">{f.ss_title} {f.ss_college ? `· ${f.ss_college}` : ''}</div></div>
            )}
            <div className="kv-row"><div className="k">UG Council</div><div className="v sans">{f.ug_council || '—'}</div></div>
            <div className="kv-row"><div className="k">PG Council</div><div className="v sans">{f.pg_council || '—'}</div></div>
            {f.has_ss && f.ss_council && (
              <div className="kv-row"><div className="k">SS Council</div><div className="v sans">{f.ss_council}</div></div>
            )}
            <div className="kv-row"><div className="k">Publications</div><div className="v">{f.publications ?? 0}</div></div>
            <div className="kv-row"><div className="k">MET / BMR</div><div className="v sans">{f.met_done ? 'MET ✓' : 'MET ✗'} · {f.bmr_done ? 'BMR ✓' : 'BMR ✗'}</div></div>
          </div>
        </div>

        <div className="card span-full">
          <div className="card-title">Promotion Readiness <span className="badge teal">NMC Gazette 2022</span></div>
          {pipeline ? (
            <div style={{display:'flex', gap:16, alignItems:'center', marginBottom:14}}>
              <div style={{fontSize:13, color:'var(--text-2)'}}>Targeting <strong style={{color:'var(--text-1)'}}>{pipeline.target}</strong></div>
              <div style={{marginLeft:'auto', fontFamily:'var(--mono)', fontSize:18, fontWeight:500, color: pipeline.readiness >= 80 ? 'var(--ok)' : pipeline.readiness >= 50 ? 'var(--warn)' : 'var(--bad)'}}>{pipeline.readiness}%</div>
            </div>
          ) : (
            <div style={{fontSize:13, color:'var(--text-3)', marginBottom:14}}>No further promotion track applicable.</div>
          )}
          <div className="readiness-grid">
            <ReadinessCell ok={promoChecks.post_pg} label="Post-PG Yrs" val={promoDisp.post_pg}/>
            <ReadinessCell ok={promoChecks.in_role} label="Time in Role" val={promoDisp.in_role}/>
            <ReadinessCell ok={promoChecks.pubs}    label="Publications" val={promoDisp.pubs}/>
            <ReadinessCell ok={promoChecks.met}     label="MET"          val={promoDisp.met}/>
            <ReadinessCell ok={promoChecks.bmr}     label="BMR"          val={promoDisp.bmr}/>
          </div>
        </div>

        {/* Promotion History · Table c1 */}
        <div className="card">
          <div className="card-title">Promotion History · Table C1</div>
          {(() => {
            const logged = history?.promotions || [];
            const currentPos = buildCurrentPosition(f);
            const inferred = extractSSMCHPromotions(f.past_experience, currentPos);
            const allPromos = [...logged, ...inferred].sort(
              (a, b) => (parseDateSafe(b.promotion_date) || 0) - (parseDateSafe(a.promotion_date) || 0)
            );
            if (allPromos.length === 0) return <div style={{color:'var(--text-3)', fontSize:13}}>No promotions logged.</div>;
            return allPromos.map(p => (
              <div className={`tl-item${p.from_past_experience ? ' tl-ssmch' : ''}`} key={p.promotion_id} data-testid={`promo-row-${p.promotion_id}`}>
                <div className="tl-date">{fmtDate(p.promotion_date)}</div>
                <div>
                  <strong>{p.previous_designation} → {p.new_designation}</strong>
                  {p.previous_department !== p.new_department && p.previous_department && <span style={{color:'var(--text-3)', fontSize:11}}> · {p.previous_department} → {p.new_department}</span>}
                  {p.from_past_experience && <span className="badge teal" style={{marginLeft:8, fontSize:9.5}}>SSMCH · Uploaded Data</span>}
                  {!p.from_past_experience && p.nmc_recognised_prev && p.nmc_recognised_prev !== "Yes" && (
                    <span className="nmc-inline-warn">Prior role: {p.nmc_recognised_prev === "No" ? "Not NMC Recognised" : "NMC Unverified"}</span>
                  )}
                  {isAdmin && (p.previous_salary || p.new_salary) && <div style={{fontSize:11, color:'var(--text-3)', marginTop:2, fontFamily:'var(--mono)'}}>{inr(p.previous_salary)} → {inr(p.new_salary)}</div>}
                  {p.notes && <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>{p.notes}</div>}
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Salary History · Table c2 — admin only */}
        {isAdmin && (
          <div className="card" data-testid="salary-history-card">
            <div className="card-title">Salary History · Table C2 <span className="badge bad">Admin</span></div>
            {(history?.salary_history || []).length === 0 && <div style={{color:'var(--text-3)', fontSize:13}}>No standalone increments logged.</div>}
            {(history?.salary_history || []).map(s => (
              <div className="tl-item" key={s.increment_id}>
                <div className="tl-date">{s.effective_from}</div>
                <div>
                  <strong style={{fontFamily:'var(--mono)'}}>{inr(s.previous_salary)} → {inr(s.new_salary)}</strong>
                  {s.reason && <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>{s.reason}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Career Timeline */}
        <div className="card span-full" data-testid="career-timeline-card">
          <div className="card-title">Career Timeline</div>
          {(() => {
            const currentPos = buildCurrentPosition(f);
            const allExp = [...(f.past_experience || [])];
            if (currentPos) allExp.push(currentPos);
            const pe = allExp.sort(
              (a, b) => (parseDateSafe(b.from_date) || 0) - (parseDateSafe(a.from_date) || 0)
            );
            // Calculate total experience in months
            let totalMonths = 0;
            for (const exp of pe) {
              const a = parseDateSafe(exp.from_date);
              const b = exp.to_date ? parseDateSafe(exp.to_date) : new Date();
              if (a && b) totalMonths += (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
            }
            if (totalMonths < 0) totalMonths = 0;
            const totalYrs = Math.floor(totalMonths / 12);
            const totalRem = totalMonths % 12;
            const totalStr = totalYrs > 0 && totalRem > 0 ? `${totalYrs}y ${totalRem}m` : totalYrs > 0 ? `${totalYrs}y` : `${totalRem}m`;
            if (pe.length === 0 && (f.life_events || []).length === 0) {
              return <div style={{color:'var(--text-3)', fontSize:13}}>No career entries recorded yet.</div>;
            }
            return (
              <>
                {pe.length > 0 && (
                  <>
                  <div style={{display:'flex', justifyContent:'flex-end', marginBottom:10}} data-testid="total-experience">
                    <span style={{fontFamily:'var(--mono)', fontSize:13, color:'var(--text-1)', background:'var(--bg-elev)', border:'1px solid var(--line)', borderRadius:6, padding:'4px 10px', fontWeight:600}}>Total Experience: {totalStr}</span>
                  </div>
                  <div className="career-tl" data-testid="career-experience-list">
                    {pe.map((exp, i) => {
                      const ssmch = isSSMCH(exp.institution);
                      const duration = calcDuration(exp.from_date, exp.to_date);
                      return (
                        <div key={i} className={`career-entry${ssmch ? ' career-ssmch' : ''}`} data-testid={`career-entry-${i}`}>
                          <div className="ce-left">
                            <div className="ce-dot" style={{background: ssmch ? 'var(--gold)' : 'var(--line)'}} />
                            {i < pe.length - 1 && <div className="ce-line" />}
                          </div>
                          <div className="ce-body">
                            <div className="ce-header">
                              <span className="ce-desig">{exp.designation || "—"}</span>
                              {ssmch && <span className="badge gold" style={{fontSize:9.5, marginLeft:6}}>SSMCH</span>}
                              {duration && <span className="ce-dur">{duration}</span>}
                            </div>
                            <div className="ce-inst">
                              {exp.institution || "—"}
                              {exp.is_current_position && exp.department && <span style={{color:'var(--text-3)'}}> · {exp.department}</span>}
                            </div>
                            <div className="ce-dates">
                              {fmtDate(exp.from_date)} — {exp.to_date ? fmtDate(exp.to_date) : <span style={{color:'var(--ok)', fontWeight:500}}>Present</span>}
                              {exp.nature_of_employment && <span className="ce-emp-type">{exp.nature_of_employment}</span>}
                            </div>
                            {!exp.is_current_position && !isSSMCH(exp.institution) && exp.nmc_recognised !== "Yes" && (
                              <div className="ce-nmc-warn" data-testid={`nmc-warn-${i}`}>
                                <span className="nmc-dot bad" />
                                {exp.nmc_recognised === "No" ? "Not NMC Recognised" : "NMC Status Unverified"}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </>
                )}
                {(f.life_events || []).length > 0 && (
                  <div style={{marginTop: pe.length > 0 ? 18 : 0}}>
                    <div style={{fontSize:12, fontWeight:500, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8}}>Life Events</div>
                    {(f.life_events || []).slice(0, 12).map(ev => (
                      <div className="tl-item" key={ev.event_id}>
                        <div className="tl-date">{fmtDate(ev.date)}</div>
                        <div><strong style={{textTransform:'capitalize'}}>{ev.event_type.replace('_',' ')}</strong>{ev.note ? ` — ${ev.note}` : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Indirect Signals — admin only */}
        {isAdmin && (
          <div className="card" style={{borderStyle:'dashed'}} data-testid="indirect-signals-card">
            <div className="card-title">Indirect Signals <span className="badge bad">Confidential</span></div>
            {signals.map((s, i) => (
              <div key={i} style={{padding:'10px 0', borderBottom:'1px solid var(--line-soft)', fontSize:13, display:'flex', alignItems:'center', gap:10}}>
                <span className={`ck ${s.ok ? 'ok' : 'no'}`}>{s.ok ? '✓' : '✗'}</span>
                <div style={{flex:1}}>
                  <div style={{fontWeight:500}}>{s.rule}</div>
                  <div style={{fontSize:11.5, color:'var(--text-3)', marginTop:2}}>⇒ {s.implies}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Life Events panel — context-aware */}
        <div className="card span-full" data-testid="life-events-card">
          <div className="card-title">Life Events {isTerminal && <span className="badge warn">Read-only · {f.status}</span>}</div>
          {!activeForm && !isTerminal && (
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:8}}>
              <button className="btn" disabled={isAbsconded} onClick={() => setActiveForm('promotion')} data-testid="event-promotion">↑ Log Promotion</button>
              <button className="btn" disabled={isAbsconded} onClick={() => setActiveForm('salary')} data-testid="event-salary">₹ Log Salary Increment</button>
              <button className="btn" onClick={() => setActiveForm('accommodation')} data-testid="event-accommodation">⌂ Update Accommodation</button>
              {isAbsconded
                ? <button className="btn" style={{color:'var(--ok)'}} onClick={() => setActiveForm('aebas')} data-testid="event-aebas-unblock">✓ Unblock AEBAS</button>
                : <button className="btn" style={{color:'var(--warn)'}} onClick={() => setActiveForm('abscond')} data-testid="event-abscond">⚠ Mark Absconded</button>
              }
              <button className="btn" style={{color:'var(--bad)'}} onClick={() => setActiveForm('relieve')} data-testid="event-relieve">⬚ Initiate Relieving</button>
            </div>
          )}
          {activeForm === 'promotion' && <PromotionForm faculty={f} onSaved={formSaved} onCancel={() => setActiveForm(null)}/>}
          {activeForm === 'salary' && <SalaryForm faculty={f} onSaved={formSaved} onCancel={() => setActiveForm(null)}/>}
          {activeForm === 'abscond' && <AbscondForm faculty={f} onSaved={formSaved} onCancel={() => setActiveForm(null)}/>}
          {activeForm === 'aebas' && <AebasUnblockForm faculty={f} onSaved={formSaved} onCancel={() => setActiveForm(null)}/>}
          {activeForm === 'relieve' && <RelieveForm faculty={f} onSaved={formSaved} onCancel={() => setActiveForm(null)}/>}
          {activeForm === 'accommodation' && <AccommodationForm faculty={f} onSaved={formSaved} onCancel={() => setActiveForm(null)}/>}
          {isTerminal && !activeForm && <div style={{color:'var(--text-3)', fontSize:13, marginTop:10}}>This faculty is {f.status.toLowerCase()}. No further life events are permitted.</div>}
        </div>
      </div>
    </div>
  );
}
